import React, { useEffect, useState } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { api } from "../lib/api";

const OCCASIONS = [
  "Wedding (mine)", "Wedding guest", "Date night", "Job interview",
  "Daytime everyday", "Photoshoot", "Holiday party", "First date",
  "Concert / festival", "Graduation",
];

const STYLES = [
  { id: "natural", label: "Natural" },
  { id: "balanced", label: "Balanced" },
  { id: "glam", label: "Glam" },
  { id: "bold", label: "Bold" },
];

const SKILLS = [
  { id: "beginner", label: "Beginner" },
  { id: "intermediate", label: "Intermediate" },
  { id: "advanced", label: "Advanced" },
];

export default function LookBuilder() {
  const navigate = useNavigate();
  const [occasion, setOccasion] = useState("");
  const [skill, setSkill] = useState("intermediate");
  const [style, setStyle] = useState("balanced");
  const [notes, setNotes] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const submit = async (e) => {
    e.preventDefault();
    if (!occasion.trim()) { setError("Please choose or enter an occasion."); return; }
    setError(null); setLoading(true);
    try {
      const { data } = await api.post("/look-builder/checkout", {
        occasion: occasion.trim(), skill, style, notes,
        origin_url: window.location.origin,
      });
      window.location.href = data.url;
    } catch (err) {
      setError(err?.response?.data?.detail || "Could not start checkout. Try again.");
      setLoading(false);
    }
  };

  return (
    <div data-testid="look-builder-page" className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-12 lg:py-20">
      <div className="overline mb-4">The Studio · AI</div>
      <h1 className="font-serif-display text-5xl sm:text-6xl font-light text-stone-900 tracking-tight">
        Build a look.
      </h1>
      <p className="mt-5 text-base text-stone-600 leading-relaxed max-w-2xl">
        Tell us the occasion. We'll generate a complete look — products to use, brushes you'll need, and a step-by-step tutorial — tailored to you. <span className="text-stone-900 font-medium">$4.99 per look.</span>
      </p>

      <form onSubmit={submit} data-testid="look-builder-form" className="mt-10 bg-white border border-[#E5E0D8] p-8 lg:p-10 space-y-7">
        <div>
          <label className="overline block mb-3" htmlFor="lb-occasion">Occasion</label>
          <div className="flex flex-wrap gap-2 mb-3">
            {OCCASIONS.map((o) => (
              <button
                key={o} type="button"
                data-testid={`lb-occasion-${o.replace(/[^a-z]/gi, "-").toLowerCase()}`}
                onClick={() => setOccasion(o)}
                className={`text-xs px-3 py-2 transition-colors ${occasion === o ? "bg-[#2C2A29] text-white" : "bg-[#F9F7F3] border border-[#E5E0D8] text-stone-700 hover:border-[#8C6A5D]"}`}
              >{o}</button>
            ))}
          </div>
          <input
            id="lb-occasion" data-testid="lb-occasion-input"
            type="text" value={occasion}
            onChange={(e) => setOccasion(e.target.value)}
            placeholder="Or type your own (e.g., outdoor garden wedding)"
            className="w-full text-sm bg-[#F9F7F3] border border-[#E5E0D8] focus:border-[#8C6A5D] outline-none px-3 py-3"
          />
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
          <div>
            <label className="overline block mb-3">Skill</label>
            <div className="flex flex-wrap gap-2">
              {SKILLS.map((s) => (
                <button
                  key={s.id} type="button"
                  data-testid={`lb-skill-${s.id}`}
                  onClick={() => setSkill(s.id)}
                  className={`text-xs px-3 py-2 transition-colors ${skill === s.id ? "bg-[#2C2A29] text-white" : "bg-[#F9F7F3] border border-[#E5E0D8] text-stone-700 hover:border-[#8C6A5D]"}`}
                >{s.label}</button>
              ))}
            </div>
          </div>
          <div>
            <label className="overline block mb-3">Style</label>
            <div className="flex flex-wrap gap-2">
              {STYLES.map((s) => (
                <button
                  key={s.id} type="button"
                  data-testid={`lb-style-${s.id}`}
                  onClick={() => setStyle(s.id)}
                  className={`text-xs px-3 py-2 transition-colors ${style === s.id ? "bg-[#2C2A29] text-white" : "bg-[#F9F7F3] border border-[#E5E0D8] text-stone-700 hover:border-[#8C6A5D]"}`}
                >{s.label}</button>
              ))}
            </div>
          </div>
        </div>

        <div>
          <label className="overline block mb-2" htmlFor="lb-notes">Notes (optional)</label>
          <textarea
            id="lb-notes" data-testid="lb-notes" rows={3}
            value={notes} onChange={(e) => setNotes(e.target.value)}
            placeholder="Eye color, skin tone, anything to avoid…"
            className="w-full text-sm bg-[#F9F7F3] border border-[#E5E0D8] focus:border-[#8C6A5D] outline-none px-3 py-3 leading-relaxed"
          />
        </div>

        {error && <p data-testid="lb-error" className="text-sm text-red-700">{error}</p>}

        <button
          data-testid="lb-submit" type="submit" disabled={loading}
          className="w-full text-xs uppercase tracking-[0.22em] bg-[#8C6A5D] hover:bg-[#70544A] disabled:bg-stone-300 text-white px-7 py-4 transition-colors"
        >
          {loading ? "Redirecting…" : "Build My Look — $4.99"}
        </button>
        <p className="text-xs text-stone-500 leading-relaxed">
          One-time payment. After Stripe checkout, your look generates instantly and saves to a private link only you can revisit.
        </p>
      </form>
    </div>
  );
}
